<template>
  <dd-modal
    :subtitle="subtitle"
    title="Merge conversations"
    class="bulk-merge-conversations-modal"
    desktop-sizing="large"
    @handle-close="$emit('edit-canceled')"
  >
    <loading-placeholder v-if="isLoading" :lines="20" />
    <template v-else>
      <div class="convo-merge-visualizer">
        <div>
          <div v-for="convo in convoMergeInfo.listMergingConvos" :key="convo.id" class="convo-chicklet">
            <customer-picker-display :customer="convo.customer" />
            <div class="convo-info"><strong>#{{convo.refNumber}}:</strong> {{convo.subject}}</div>
          </div>
        </div>
        <i class="ddi ddi-merge-down rotate-270" />
        <div>
          <div class="convo-chicklet merged-into">
            <customer-picker-display :customer="convoMergeInfo.consolidatedConvo.customer" />
            <div class="convo-info"><strong>#{{convoMergeInfo.consolidatedConvo.refNumber}}:</strong> {{convoMergeInfo.consolidatedConvo.subject}}</div>
          </div>
        </div>
      </div></template>
    <!-- FOOTER -->
    <template v-if="!isLoading" #footer>
      <span>
        <dd-checkbox v-model="userAcceptanceClicked" dd-size="large">
          I understand this is irreversible.
          (<a href="https://www.donedone.com/help-docs/merging-conversations-in-a-mailbox" class="just-a-link" target="_blank">How does merging work?</a>)
        </dd-checkbox>
        <dd-button
          :is-loading="isSubmitting"
          :is-disabled="!userAcceptanceClicked"
          :color="ColorAssistant.getEntityColor(EEntities.MAILBOXES)"
          @click="handleMerge()"
        >Merge {{selectedConvoIds.length}} conversations</dd-button>
        <dd-button type="flat" @click="$emit('edit-canceled')">Cancel</dd-button>
      </span>
    </template>
  </dd-modal>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'
import TextAssistant from '@/helpers/text-assistant'

/* Data types */
import { EEntities } from '@/types'
import { IConvoMergeInfo, IPersonBase } from '@/api/types/response.types'

/* API */
import ConversationsAPI from '@/api/modules/convos.api'

export default defineComponent({
  name: 'BulkMergeConversationsModal',
  props: {
    mailboxId: {
      type: Number,
      required: true,
    },
    selectedConvoIds: {
      type: Array as () => number[],
      default: (): number[] => [],
    },
  },

  emits: ['edit-canceled', 'edit-completed'],
  data() {
    return {
      EEntities,
      ColorAssistant,
      isLoading: true,
      isSubmitting: false,
      userAcceptanceClicked: false,

      convoMergeInfo: {} as IConvoMergeInfo,
    }
  },
  computed: {
    subtitle(): string {
      if (this.isLoading) {
        return ''
      }
      return `Comments, replies, tags, and linked tasks will be merged into the oldest conversation, 
          #${this.convoMergeInfo.consolidatedConvo.refNumber}.`
    },
    potentialCustomers(): IPersonBase[] {
      const result = this.convoMergeInfo.listMergingConvos.map(c => c.customer)
      result.push(this.convoMergeInfo.consolidatedConvo.customer)

      const uniqueResults = result.filter(
        (value, index, self) =>
          self.findIndex(item => item.id === value.id) === index,
      )

      const sortedUniqueResults = uniqueResults.sort(
        (a: IPersonBase, b: IPersonBase) => {
          if (a.name < b.name) {
            return -1
          }
          if (a.name > b.name) {
            return 1
          }
          return 0
        },
      )

      return sortedUniqueResults
    },
  },
  watch: {},
  async created() {
    this.isLoading = true
    this.convoMergeInfo = await ConversationsAPI.get_MergePreview(
      this.mailboxId,
      TextAssistant.convertNumberArrayToStringList(this.selectedConvoIds),
    )
    this.isLoading = false
  },
  methods: {
    async handleMerge() {
      this.isSubmitting = true
      let submitSucceeded = false
      try {
        const response = await ConversationsAPI.put_Merge(
          this.mailboxId,
          this.selectedConvoIds,
        )
        this.$messaging.showSuccess(response.message)
        submitSucceeded = true
      } catch (error) {
        this.$error.emit(error, true)
      }

      this.isSubmitting = false
      // Do update...
      if (submitSucceeded) {
        this.$emit('edit-completed')
      }
    },
  },
})
</script>

<style lang="scss">
.bulk-merge-conversations-modal {
  .convo-merge-visualizer {
    width: 100%;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    @include flex-row-horizontal-spacing(10px);

    .ddi {
      font-size: 24px;
      color: var(--color-text-third);
    }

    > div {
      position: relative;
      display: flex;
      width: 46%;
      flex-direction: column;
      @include flex-column-vertical-spacing(4px);

      .convo-chicklet {
        padding: 8px;
        border-radius: 8px;
        border: $border--dotted-light;
        @include standard-box-shadow;

        &.merged-into {
          background-color: var(--color-background-green);
        }
        .convo-info {
          padding: 6px 0 6px 8px;
          @include single-line-ellipsis;
        }
      }
    }
  }

  /* Custom footer styling */
  .dd-modal__footer > span {
    @include flex-row-horizontal-spacing(10px);
    .el-checkbox {
      margin-top: 6px;
    }
  }
}
</style>
